import React, { useEffect, useState, useCallback } from 'react';
import Header from '../common/Header';
import EditProfileModal from './EditProfileModal';
import { useNavigate, useLocation } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatNumber } from '../../utils/numberUtils';
import axios from 'axios';
import { avatarPaths } from '../../utils/avatarUtils';
import AchievementsDisplay from './AchievementsDisplay';
import XpLevelDisplay from './XpLevelDisplay';
import { Trophy } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { requestUserLocation } from '../../utils/geolocation';

const AvatarDisplay = ({ avatar, username }) => {
    return (
        <div className="w-20 h-20">
            <svg
                viewBox={avatarPaths.jersey.viewBox}
                className="w-full h-full rounded-full"
                style={{ color: avatar.avatar_color }}
            >
                {avatarPaths.jersey.content({
                    username: username?.toUpperCase() || '',
                    jerseyNumber: avatar.jersey_number || '00'
                })}
            </svg>
        </div>
    );
};

// Styling for admin-authored Markdown (competition instructions / footer copy).
const MarkdownHeading = ({ node, ...props }) => (
    <h3 className="mb-1 text-xl text-popashot-blue" {...props} />
);

// index.css sets font size/color directly on p and ul, so size classes must go on each element
// (a size on the wrapper div would be ignored).
const buildMarkdownComponents = (textClass) => ({
    h1: MarkdownHeading,
    h2: MarkdownHeading,
    h3: MarkdownHeading,
    p: ({ node, ...props }) => <p className={`mb-2 last:mb-0 whitespace-pre-line ${textClass}`} {...props} />,
    strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
    ol: ({ node, ...props }) => <ol className={`mb-2 list-decimal list-inside space-y-1 ${textClass}`} {...props} />,
    ul: ({ node, ...props }) => <ul className={`mb-2 list-disc list-inside space-y-1 ${textClass}`} {...props} />,
    a: ({ node, href, ...props }) => {
        const isMailto = href?.startsWith('mailto:');
        return (
            <a
                href={href}
                {...(isMailto ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                className="text-popashot-blue underline"
                {...props}
            />
        );
    },
});

const instructionsMarkdown = buildMarkdownComponents('text-sm text-gray-600');
const footerMarkdown = buildMarkdownComponents('text-xs text-gray-500');

// Uploads a video for a PlayerScore. One upload runs at a time; onUploaded runs after each successful one.
const useScoreVideoUpload = (onUploaded) => {
    const [uploadingScoreId, setUploadingScoreId] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadMessage, setUploadMessage] = useState(null);

    const uploadVideo = (playerScoreId, file) => {
        setUploadingScoreId(playerScoreId);
        setUploadProgress(0);
        setUploadMessage(null);

        const formData = new FormData();
        formData.append('player_score_id', playerScoreId);
        formData.append('video', file);

        const token = localStorage.getItem('access_token');
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
                const percentComplete = Math.round((e.loaded / e.total) * 100);
                setUploadProgress(percentComplete);
                if (percentComplete === 100) {
                    setTimeout(() => {
                        setUploadProgress(101);
                        setUploadMessage({ type: 'info', text: 'Processing video...' });
                    }, 1000);
                }
            }
        });

        xhr.addEventListener('load', () => {
            if (xhr.status === 200) {
                setUploadMessage({ type: 'success', text: 'Video uploaded successfully!' });
                onUploaded();
                setTimeout(() => {
                    setUploadingScoreId(null);
                    setUploadProgress(0);
                    setUploadMessage(null);
                }, 2000);
            } else {
                const error = JSON.parse(xhr.responseText);
                setUploadMessage({ type: 'error', text: error.error || 'Upload failed' });
                setUploadingScoreId(null);
                setUploadProgress(0);
            }
        });

        xhr.addEventListener('error', () => {
            setUploadMessage({ type: 'error', text: 'Network error during upload' });
            setUploadingScoreId(null);
            setUploadProgress(0);
        });

        xhr.open('POST', `${process.env.REACT_APP_API_URL}/api/competitions/upload-video/`);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(formData);
    };

    const handleFileSelect = (event, playerScoreId) => {
        const file = event.target.files[0];
        if (!file) return;
        if (!file.type.startsWith('video/')) {
            setUploadMessage({ type: 'error', text: 'Please select a video file' });
            return;
        }
        const maxSize = 2 * 1024 * 1024 * 1024;
        if (file.size > maxSize) {
            setUploadMessage({ type: 'error', text: 'File size exceeds 2GB limit' });
            return;
        }
        uploadVideo(playerScoreId, file);
    };

    return { uploadingScoreId, uploadProgress, uploadMessage, handleFileSelect };
};

const VideoUploadMessage = ({ message }) => {
    if (!message) return null;
    return (
        <div className={`mb-4 p-3 rounded ${
            message.type === 'success' ? 'bg-popashot-blue/10 text-popashot-blue' :
            message.type === 'info' ? 'bg-popashot-red/10 text-popashot-red' :
            'bg-red-100 text-red-800'
        }`}>
            <p className="text-sm font-medium">{message.text}</p>
        </div>
    );
};

const VideoUploadProgress = ({ progress }) => {
    if (progress <= 0) return null;
    if (progress > 100) {
        return (
            <div className="mt-2">
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-popashot-blue h-2 rounded-full w-full animate-pulse"></div>
                </div>
                <p className="text-xs text-gray-500 mt-1 text-center">Processing...</p>
            </div>
        );
    }
    return (
        <div className="mt-2">
            <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                    className="bg-popashot-blue h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                ></div>
            </div>
            <p className="text-xs text-gray-500 mt-1 text-center">{progress}%</p>
        </div>
    );
};

// Upload button for one score (anything with id and has_video), or "✓ Video Uploaded" once it has one.
const VideoUploadButton = ({ score, upload }) => {
    if (score.has_video) {
        return (
            <span className="inline-block px-3 py-2 bg-popashot-blue/10 text-popashot-blue text-xs font-medium rounded">
                ✓ Video Uploaded
            </span>
        );
    }
    const busy = upload.uploadingScoreId !== null;
    return (
        <>
            <input
                type="file"
                accept="video/*"
                onChange={(event) => upload.handleFileSelect(event, score.id)}
                className="hidden"
                id={`video-upload-${score.id}`}
                disabled={busy}
            />
            <label
                htmlFor={`video-upload-${score.id}`}
                className={`inline-block px-3 py-2 bg-popashot-red text-white text-xs font-bold rounded uppercase cursor-pointer ${busy ? 'opacity-50 cursor-not-allowed' : 'hover:bg-red-700'}`}
            >
                {upload.uploadingScoreId === score.id ? 'UPLOADING...' : 'UPLOAD VIDEO'}
            </label>
        </>
    );
};

// Admin-written copy above and below the video verification UI (same in the verify section and the round list).
const CompetitionInstructions = ({ comp }) => {
    if (!comp.instructions_header && !comp.instructions) return null;
    return (
        <div className="mb-6">
            {comp.instructions_header && (
                <h3 className="mb-1 text-xl text-popashot-blue">{comp.instructions_header}</h3>
            )}
            {comp.instructions && (
                <div className="mb-2">
                    <ReactMarkdown components={instructionsMarkdown}>{comp.instructions}</ReactMarkdown>
                </div>
            )}
        </div>
    );
};

const CompetitionFooter = ({ comp }) => {
    if (!comp.footer_content) return null;
    return (
        <div className="mt-4">
            <ReactMarkdown components={footerMarkdown}>{comp.footer_content}</ReactMarkdown>
        </div>
    );
};

// onRefresh reloads the competitions feed, which is where the player's latest score comes from.
const CompetitionCard = ({ comp, onRefresh }) => {
    const [expanded, setExpanded] = useState(false);
    const [loadingScore, setLoadingScore] = useState(false);
    const cardRef = React.useRef(null);
    const upload = useScoreVideoUpload(onRefresh);

    const showVideoSection = comp.video_verification && comp.user_state !== null;
    const recentScore = comp.user_state?.latest_score ?? null;

    const handleToggle = async () => {
        if (!expanded) {
            // Load score FIRST, then expand
            setLoadingScore(true);
            await onRefresh();
            setLoadingScore(false);
            // Small delay to ensure DOM has updated with loaded content
            setTimeout(() => {
                setExpanded(true);
                // Use requestAnimationFrame to ensure scroll happens after paint
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        if (cardRef.current) {
                            const headerHeight = 64; // 16 * 4 = 64px (h-16 from header)
                            const elementPosition = cardRef.current.getBoundingClientRect().top;
                            const targetPosition = elementPosition + window.pageYOffset - headerHeight - 16; // Extra 16px padding

                            // Custom scroll animation
                            const startPosition = window.pageYOffset;
                            const distance = targetPosition - startPosition;
                            const duration = 1000; // 1 second
                            let startTime = null;

                            function animation(currentTime) {
                                if (startTime === null) startTime = currentTime;
                                const timeElapsed = currentTime - startTime;
                                const progress = Math.min(timeElapsed / duration, 1);

                                // Easing function for smooth animation
                                const ease = progress < 0.5
                                    ? 2 * progress * progress
                                    : 1 - Math.pow(-2 * progress + 2, 2) / 2;

                                window.scrollTo(0, startPosition + (distance * ease));

                                if (timeElapsed < duration) {
                                    requestAnimationFrame(animation);
                                }
                            }

                            requestAnimationFrame(animation);
                        }
                    });
                });
            }, 50);
        } else {
            // Collapsing - just close immediately
            setExpanded(false);
        }
    };

    return (
        <motion.div
            ref={cardRef}
            className="mt-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
        >
            <div className="bg-white rounded-lg border border-popashot-grey-light p-4">
                {comp.banner_image_url && (
                    <img
                        src={comp.banner_image_url}
                        alt={comp.name}
                        className="w-3/4 h-auto mb-3 rounded mx-auto"
                    />
                )}
                {comp.tagline && (
                    <p className="text-center text-sm text-gray-600 mb-4">{comp.tagline}</p>
                )}
                {comp.button_text && (
                    showVideoSection ? (
                        <button onClick={handleToggle} className="btn-primary w-full">
                            {expanded ? 'Hide' : comp.button_text}
                        </button>
                    ) : (
                        comp.button_url && (
                            <a
                                href={comp.button_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-primary hover:text-white"
                            >
                                {comp.button_text}
                            </a>
                        )
                    )
                )}

                {showVideoSection && (
                    <motion.div
                        initial={false}
                        animate={{
                            maxHeight: expanded ? '1000px' : '0px',
                            opacity: expanded ? 1 : 0,
                            marginTop: expanded ? '12px' : '0px',
                            paddingTop: expanded ? '12px' : '0px'
                        }}
                        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                        style={{ overflow: 'hidden' }}
                        className="border-t border-gray-200"
                    >
                        <CompetitionInstructions comp={comp} />

                        <VideoUploadMessage message={upload.uploadMessage} />

                        <div>
                            <h3 className="mb-2 text-xl text-popashot-blue">{comp.score_header || 'Your Score'}</h3>
                            {loadingScore ? (
                                <div className="text-center py-4">
                                    <p className="text-sm text-gray-500">Loading score...</p>
                                </div>
                            ) : !recentScore ? (
                                <div className="border border-dashed border-gray-300 rounded p-3 bg-gray-50">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="font-bold text-lg text-gray-400">---</p>
                                            <p className="text-xs text-gray-400">No qualifying score yet. Keep playing!</p>
                                        </div>
                                        <span className="inline-block px-3 py-2 bg-popashot-red text-white text-xs font-bold rounded uppercase cursor-not-allowed opacity-50">
                                            UPLOAD VIDEO
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div className="border border-gray-200 rounded p-3">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <p className="font-bold text-lg text-popashot-blue">{recentScore.score.toLocaleString()}</p>
                                            <p className="text-xs text-gray-400">
                                                {new Date(recentScore.created_at).toLocaleDateString()} {new Date(recentScore.created_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZoneName: 'short' })}
                                            </p>
                                        </div>
                                        <div>
                                            <VideoUploadButton score={recentScore} upload={upload} />
                                        </div>
                                    </div>
                                    {upload.uploadingScoreId === recentScore.id && (
                                        <VideoUploadProgress progress={upload.uploadProgress} />
                                    )}
                                </div>
                            )}
                        </div>

                        <CompetitionFooter comp={comp} />
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
};

// With video verification on, each played round gets its own upload button, with the competition's instructions
// above and footer below. This is the only layout that can verify every game; Team totals leagues verify just the
// latest one through CompetitionCard (see the note where it renders). onRefresh reloads the competitions feed.
const LeagueGameScores = ({ comp, leagueStats, heading, dimmed, onRefresh }) => {
    const videoVerification = comp.video_verification;
    const upload = useScoreVideoUpload(onRefresh);
    const slots = Array.from({ length: leagueStats.max_weekly_games || 0 }, (_, i) => {
        const game = leagueStats.games?.find(g => g.game_number === i + 1);
        return { number: i + 1, score: game ? game.score : null, game };
    });

    return (
        <div className="mt-6">
            {videoVerification && <CompetitionInstructions comp={comp} />}
            <h3 className={`text-xl mb-2 ${dimmed ? 'opacity-50' : ''}`}>{heading}</h3>
            <VideoUploadMessage message={upload.uploadMessage} />
            <div className={`bg-white rounded-lg border border-popashot-grey-light overflow-hidden ${dimmed ? 'opacity-50' : ''}`}>
                {slots.map((slot, index) => (
                    <motion.div
                        key={slot.number}
                        className="py-3 px-4 border-b border-gray-100 last:border-b-0"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-gray-600">Round {slot.number}</span>
                            <div className="flex items-center gap-3">
                                <span className={`text-sm ${slot.score !== null ? 'font-bold text-popashot-blue' : 'text-gray-400'}`}>
                                    {slot.score !== null ? slot.score.toLocaleString('en-US') : '—'}
                                </span>
                                {videoVerification && slot.game && (
                                    <VideoUploadButton score={slot.game} upload={upload} />
                                )}
                            </div>
                        </div>
                        {videoVerification && slot.game && upload.uploadingScoreId === slot.game.id && (
                            <VideoUploadProgress progress={upload.uploadProgress} />
                        )}
                    </motion.div>
                ))}
            </div>
            {videoVerification && <CompetitionFooter comp={comp} />}
        </div>
    );
};

const Profile = () => {
    const [userData, setUserData] = useState(null);
    const [allAchievements, setAllAchievements] = useState([]);
    // all competitions from the feed (active + pre-season); drives competition cards above player stats
    const [allCompetitions, setAllCompetitions] = useState([]);
    const activeLeague = allCompetitions.find(c => c.competition_type === 'league');
    // null when no league is active OR user is not enrolled — used to gate league UI
    const leagueStats = activeLeague?.user_state?.enrolled ? activeLeague.user_state : null;
    // Display variant of League, with one known exception: for this layout the server skips the team stats
    // (LeagueHandler.get_user_state), because a bracket puts every player on one team and they cost a query per
    // teammate. That is already behavior, not just display, so any further divergence should promote the bracket
    // to its own competition type instead of adding more layout checks.
    const perGameLayout = activeLeague?.stats_layout === 'per_game';
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [activeQueues, setActiveQueues] = useState([]);
    const [joiningQueue, setJoiningQueue] = useState(false);

    const navigate = useNavigate();
    const location = useLocation();

    // Check if we should open the edit modal on mount
    useEffect(() => {
        if (location.state?.openEditModal) {
            setIsEditModalOpen(true);
            // Clear the state so it doesn't reopen on refresh
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location, navigate]);

    // 1. Wrap in useCallback with any necessary dependencies
    const fetchUserData = useCallback(async () => {
        try {
            const token = localStorage.getItem('access_token');
            const userResponse = await fetch(
                `${process.env.REACT_APP_API_URL}/api/users/profile/`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!userResponse.ok) throw new Error('Failed to fetch user data');
            const userDataJson = await userResponse.json();
            setUserData(userDataJson);
        } catch (error) {
            console.error('Error fetching user data:', error);
            navigate('/login');
        }
    }, [navigate]);

    // Keeps what is on screen when a refresh fails, so a flaky connection never blanks the competitions.
    const fetchCompetitions = useCallback(async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/competitions/`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (!response.ok) return;
            setAllCompetitions(await response.json());
        } catch (error) {
            console.error('Error fetching competition data:', error);
        }
    }, []);

    const fetchActiveQueues = useCallback(async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/events/queues/active/`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.ok) {
                const queues = await response.json();
                setActiveQueues(queues);
            }
        } catch (error) {
            console.error('Error fetching active queues:', error);
        }
    }, []);

    useEffect(() => {
        const fetchAchievements = async () => {
            try {
                const token = localStorage.getItem('access_token');
                const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/achievements/`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setAllAchievements(response.data);
            } catch (error) {
                console.error('Error fetching achievements:', error);
            }
        };

        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/login');
            return;
        }

        // 2. Call the memoized function and include it in the dependency array
        fetchUserData();
        fetchAchievements();
        fetchCompetitions();
        fetchActiveQueues();
    }, [fetchUserData, fetchCompetitions, fetchActiveQueues, navigate]);

    // Scores change while the player is away playing or filming; refresh when they come back to the page.
    // Only while there are competitions to show.
    const hasCompetitions = allCompetitions.length > 0;
    useEffect(() => {
        if (!hasCompetitions) return;
        const refreshWhenVisible = () => {
            if (document.visibilityState === 'visible') fetchCompetitions();
        };
        document.addEventListener('visibilitychange', refreshWhenVisible);
        return () => document.removeEventListener('visibilitychange', refreshWhenVisible);
    }, [hasCompetitions, fetchCompetitions]);

    // Polling for queue updates
    useEffect(() => {
        let interval;
        
        // Only poll if user is in a queue AND not playing
        if (userData?.current_queue_entry && userData.current_queue_entry.status !== 'playing') {
            interval = setInterval(() => {
                fetchUserData(); // Your existing function
            }, 5000); // Poll every 5 seconds
        }
        
        // Cleanup interval when component unmounts or user leaves queue
        return () => {
            if (interval) {
                clearInterval(interval);
            }
        };
    }, [userData?.current_queue_entry?.status, fetchUserData]);

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        navigate('/login');
    };

    const handleLeagueModeToggle = async () => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/users/profile/`,
                {
                    method: 'PATCH',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        league_mode_enabled: !userData?.league_mode_enabled
                    }),
                }
            );
            if (!response.ok) throw new Error('Failed to update league mode');
            const updatedUser = await response.json();
            setUserData(updatedUser);
        } catch (error) {
            console.error('Error updating league mode:', error);
        }
    };

    const handleLeaveQueue = async () => {
        try {
            const token = localStorage.getItem('access_token');
            await fetch(`${process.env.REACT_APP_API_URL}/api/events/queues/${userData.current_queue_entry.queue_code}/leave/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            fetchUserData(); // Refresh to remove queue status from UI
            fetchActiveQueues(); // Refresh active queues list
        } catch (error) {
            console.error('Error leaving queue:', error);
        }
    };

    const handleJoinQueue = async (queue) => {
        setJoiningQueue(true);
        try {
            const token = localStorage.getItem('access_token');
            
            // Request user's location
            const location = await requestUserLocation();
            
            // Join queue with location data
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/api/events/queues/${queue.code}/join/`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        latitude: location.latitude,
                        longitude: location.longitude
                    })
                }
            );

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Failed to join queue');
            }

            // Refresh user data to show queue status
            await fetchUserData();
            await fetchActiveQueues(); // Refresh to hide join buttons immediately
            
        } catch (error) {
            alert(error.message);
        } finally {
            setJoiningQueue(false);
        }
    };

    return (
        <>
            <div className="pt-8 px-3">
                <Header 
                    onEditProfile={() => setIsEditModalOpen(true)}
                    onLogout={handleLogout}
                    isStaff={userData?.is_staff}
                />

                {/* User Info */}
                <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                            {!userData ? (
                                <div className="h-20 w-20 rounded-full bg-gray-200" />
                            ) : (
                                userData.avatar_color ? (
                                    <div key="avatar">
                                        <AvatarDisplay
                                            avatar={{
                                                avatar_color: userData.avatar_color,
                                                jersey_number: userData.jersey_number
                                            }}
                                            username={userData.username}
                                        />
                                    </div>
                                ) : (
                                    <div
                                        key="initial"
                                        className="h-20 w-20 rounded-full bg-indigo-100 flex items-center justify-center"
                                    >
                                        <span className="text-2xl text-indigo-600">
                                            {userData.username?.charAt(0)?.toUpperCase()}
                                        </span>
                                    </div>
                                )
                            )}
                        </div>
                        <div className="min-w-0 flex-1">
                            {!userData ? (
                                <div>
                                    <div className="h-8 bg-gray-200 rounded w-3/4 mb-2" />
                                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                                </div>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <div>
                                        <h3 className="leading-6 truncate">{userData.username}</h3>
                                        <span className="text-gray-500 truncate block">{userData.email}</span>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </div>
                    
                </div>

                {/* QR Code */}
                <div className="mt-2">
                    <div className="flex justify-center">
                        {!userData ? (
                            <div className="bg-gray-200 rounded-lg w-[250px] h-[250px]" />
                        ) : (
                            <motion.div 
                                className="bg-white rounded-lg max-w-[200px]"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                            >
                                <QRCodeSVG
                                    value={userData.uuid || ''}
                                    size="100%"
                                    level="H"
                                    includeMargin={true}
                                    fgColor={userData?.league_mode_enabled && userData.team ? "#1F4476" : "#B3222A"}
                                />
                            </motion.div>
                        )}
                    </div>
                    
                </div>

                {/* Join Virtual Queue Button - Only show location-based queues */}
                {activeQueues.filter(q => q.require_location).length > 0 && !userData?.current_queue_entry && (
                    <motion.div 
                        className="mt-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        <div className="bg-white rounded-lg border border-popashot-grey-light p-4">
                            {activeQueues.filter(q => q.require_location).map(queue => (
                                <button
                                    key={queue.code}
                                    onClick={() => handleJoinQueue(queue)}
                                    disabled={joiningQueue}
                                    className="btn-primary w-full mb-2 last:mb-0"
                                >
                                    {joiningQueue ? 'Joining...' : `Join ${queue.name}`}
                                </button>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Competition cards — one card per competition that has visual content.
                    Active leagues suppress their card here; one with video verification renders after the league section instead. */}
                {allCompetitions.map(comp => {
                    if (comp.competition_type === 'league' && comp.user_state !== null) return null;
                    const hasBannerContent = comp.banner_image_url || comp.tagline || comp.button_text;
                    const hasVideoSection = comp.video_verification && comp.user_state !== null;
                    if (!hasBannerContent && !hasVideoSection) return null;
                    return <CompetitionCard key={comp.id} comp={comp} onRefresh={fetchCompetitions} />;
                })}

                {/* Queue Status */}
                {userData?.current_queue_entry && userData.current_queue_entry.status !== 'playing' && (
                    <motion.div 
                        className="mt-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        <div className="bg-white rounded-lg border border-popashot-grey-light p-4">
                            <div className="text-center">
                                <div className="mb-3">
                                    <p className={`text-lg font-bold ${userData?.team ? 'text-popashot-red' : 'text-popashot-blue'}`}>
                                        You are #{userData.current_queue_entry.position} in the {userData.current_queue_entry.queue_name} queue
                                    </p>
                                </div>
                                <div className="mb-2">
                                    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                                        userData.current_queue_entry.status === 'ready' 
                                            ? 'bg-green-100 text-green-800' 
                                            : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        <span style={{ 
                                            animation: 'dramaticPulse .75s ease-in-out infinite',
                                            animationName: 'dramaticPulse'
                                        }}>
                                            <style>{`
                                                @keyframes dramaticPulse {
                                                    0%, 100% { opacity: 1; }
                                                    50% { opacity: 0.2; }
                                                }
                                            `}</style>
                                            {userData.current_queue_entry.status === 'ready' ? '🟢' : '⏳'}
                                        </span>
                                        {' '}
                                        {userData.current_queue_entry.status === 'ready' ? 'Get in line and ready to play!' : 'Waiting'}
                                    </span>
                                </div>
                                <div className="mb-4">
                                    <p className="text-xs text-gray-500">
                                        {new Date().toLocaleTimeString([], { 
                                            hour: 'numeric', 
                                            minute: '2-digit'
                                        })} ({new Date().toLocaleTimeString([], { 
                                            timeZoneName: 'short'
                                        }).split(' ').pop()})
                                    </p>
                                </div>
                                <button 
                                    className="btn-primary w-full"
                                    onClick={handleLeaveQueue}
                                >
                                    Leave Queue
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Player Stats */}
                <div className="mt-4">
                    <h3 className={`mb-2 text-xl ${userData?.team ? 'text-popashot-red' : ''}`}>Player Stats</h3>
                    <div className="bg-white grid grid-cols-3 text-center rounded-lg border border-popashot-grey-light">
                        {!userData ? (
                            <>
                                {[1, 2, 3].map((i) => (
                                    <div key={`skeleton-${i}`} className="p-4">
                                        <div className="h-8 bg-gray-200 rounded mb-2"></div>
                                        <div className="h-4 bg-gray-200 rounded w-2/3 mx-auto"></div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <>
                                <motion.div
                                    className="p-4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.3 }}
                                >
                                    <p className={`text-xl font-extrabold ${userData?.team ? 'text-popashot-red' : 'text-popashot-blue'}`}>
                                        {formatNumber(userData.user_stat?.total_score || 0)}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">Total Score</p>
                                </motion.div>
                                <motion.div
                                    className="p-4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.4 }}
                                >
                                    <p className={`text-xl font-extrabold ${userData?.team ? 'text-popashot-red' : 'text-popashot-blue'}`}>
                                        {userData.user_stat?.highscore || 0}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">High Score</p>
                                </motion.div>
                                <motion.div
                                    className="p-4"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.5 }}
                                >
                                    <p className={`text-xl font-extrabold ${userData?.team ? 'text-popashot-red' : 'text-popashot-blue'}`}>
                                        {userData.user_stat?.avg_score || 0}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-1">Avg. Score</p>
                                </motion.div>
                            </>
                        )}
                    </div>
                </div>

                {/* League Mode Toggle - Only show when league is active and user is enrolled */}
                {leagueStats && (
                    <motion.div 
                        className="mt-8 border-t border-b border-gray-300 py-4"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 }}
                    >
                        <div className="flex justify-between items-center">
                            <div className="flex items-center mr-4">
                                <div className="w-10 h-10 bg-popashot-blue rounded-full flex items-center justify-center mr-3">
                                    <Trophy className="w-6 h-6 text-white" />
                                </div>
                                <div className="flex flex-col gap-1">
                                    <h3 className="text-xl leading-none">{activeLeague?.toggle_title || 'League Mode'}</h3>
                                    <p className="text-xs text-gray-600">{activeLeague?.toggle_description || 'Count games towards weekly team stats'}</p>
                                </div>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={userData?.league_mode_enabled && userData.team ? true : false}
                                    onChange={handleLeagueModeToggle}
                                />
                                <div className={`w-11 h-6 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer transition-colors duration-100 ${userData?.league_mode_enabled && userData.team ? 'bg-popashot-blue' : 'bg-gray-300'}`}>
                                    <div className={`absolute top-[2px] left-[2px] bg-white border border-gray-300 rounded-full h-5 w-5 transition-transform duration-100 ${userData?.league_mode_enabled && userData.team ? 'translate-x-full' : 'translate-x-0'}`}></div>
                                </div>
                            </label>
                        </div>
                    </motion.div>
                )}

                {/* Per-game scores - alternative to the team stats block below */}
                {leagueStats && perGameLayout && (
                    <LeagueGameScores
                        comp={activeLeague}
                        leagueStats={leagueStats}
                        heading={activeLeague?.stats_heading || 'Your Games'}
                        dimmed={!userData?.league_mode_enabled}
                        onRefresh={fetchCompetitions}
                    />
                )}

                {/* Team Stats - Only show when league is active and user is enrolled */}
                {leagueStats && !perGameLayout && (
                    <div className="mt-6">
                        {/* Teammates Table */}
                        <div className="mt-6">
                            <h3 className={`text-xl mb-2 ${!userData?.league_mode_enabled ? 'opacity-50' : ''}`}>{activeLeague?.stats_heading || 'Team Stats (Weekly)'}</h3>
                            
                            {/* Column Headers - Outside the container */}
                            {leagueStats && (
                                <div className={`flex items-center py-2 px-4 mb-1 ${!userData?.league_mode_enabled ? 'opacity-50' : ''}`}>
                                    <div className="font-extrabold truncate text-lg text-gray-500 flex-1 min-w-0">{leagueStats?.team_name || userData?.team?.name || 'Unknown Team'}</div>
                                    <div className="flex space-x-2 flex-shrink-0">
                                        <div className="text-center text-xs text-gray-500 w-16">Games</div>
                                        <div className="text-center text-xs text-gray-500 w-16">Avg. Score</div>
                                        <div className="text-center text-xs text-gray-500 w-16">Total*</div>
                                    </div>
                                </div>
                            )}
                            
                            <div className={`bg-white rounded-lg border border-popashot-grey-light overflow-hidden ${!userData?.league_mode_enabled ? 'opacity-50' : ''}`}>
                                {!leagueStats ? (
                                    // Loading skeleton
                                    <div className="p-4">
                                        {[1, 2, 3, 4].map((i) => (
                                            <div key={`teammate-skeleton-${i}`} className="flex items-center py-3 border-b border-gray-100 last:border-b-0">
                                                <div className="h-5 bg-gray-200 rounded flex-1 min-w-0 mr-4"></div>
                                                <div className="flex space-x-2 flex-shrink-0">
                                                    <div className="h-5 bg-gray-200 rounded w-16"></div>
                                                    <div className="h-5 bg-gray-200 rounded w-16"></div>
                                                    <div className="h-5 bg-gray-200 rounded w-16"></div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <div className="w-full">
                                            {/* Teammate Rows */}
                                            {leagueStats.teammates?.map((teammate, index) => (
                                                <motion.div 
                                                    key={teammate.username}
                                                    className="flex items-center py-3 px-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                                >
                                                    <div className={`flex-1 min-w-0 text-sm ${teammate.username === userData?.username ? 'font-bold text-popashot-blue' : 'font-medium text-gray-600'}`}>
                                                        <span className="truncate block">
                                                            {index + 1}. {teammate.username.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className="flex space-x-2 flex-shrink-0">
                                                        <div className={`text-center text-sm w-16 ${teammate.username === userData?.username ? 'text-popashot-blue font-semibold' : 'text-gray-600 font-normal'}`}>
                                                            {teammate.weekly_games_played}/{leagueStats.max_weekly_games}
                                                        </div>
                                                        <div className={`text-center text-sm w-16 ${teammate.username === userData?.username ? 'text-popashot-blue font-semibold' : 'text-gray-600 font-normal'}`}>
                                                            {teammate.weekly_avg_score}
                                                        </div>
                                                        <div className={`text-center text-sm w-16 ${teammate.username === userData?.username ? 'text-popashot-blue font-semibold' : 'text-gray-600 font-normal'}`}>
                                                            {teammate.weekly_total_score.toLocaleString('en-US')}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                            
                                            {/* Team Totals Row */}
                                            <div className="flex items-center py-3 px-4 border-t border-gray-300">
                                                <div className="font-bold leading-none text-gray-600 flex-1 min-w-0">
                                                    Total
                                                </div>
                                                <div className="flex space-x-2 flex-shrink-0">
                                                    <div className="text-center font-bold text-sm text-popashot-blue w-16">
                                                        {leagueStats.team_total_games_played}/{leagueStats.team_max_possible_games}
                                                    </div>
                                                    <div className="text-center font-bold text-sm text-popashot-blue w-16">
                                                        {leagueStats.team_avg}
                                                    </div>
                                                    <div className="text-center font-bold text-sm text-popashot-blue w-16">
                                                        {leagueStats.team_total_score.toLocaleString('en-US')}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Opponent Stats - Only show if user has a team and opponent exists */}
                        {userData?.team && leagueStats?.opponent && (
                            <div className="mt-6">
                                {/* Column Headers - Outside the container */}
                                <div className={`flex items-center py-2 px-4 mb-1 ${!userData?.league_mode_enabled ? 'opacity-50' : ''}`}>
                                    <div className="font-extrabold truncate text-lg text-gray-500 flex-1 min-w-0">{leagueStats.opponent.name}</div>
                                    <div className="flex space-x-2 flex-shrink-0">
                                        <div className="text-center text-xs text-gray-500 w-16">Games</div>
                                        <div className="text-center text-xs text-gray-500 w-16">Avg. Score</div>
                                        <div className="text-center text-xs text-gray-500 w-16">Total*</div>
                                    </div>
                                </div>
                                
                                <div className={`bg-white rounded-lg border border-popashot-grey-light overflow-hidden ${!userData?.league_mode_enabled ? 'opacity-50' : ''}`}>
                                    <div className="overflow-x-auto">
                                        <div className="w-full">
                                            {/* Opponent Teammate Rows */}
                                            {leagueStats.opponent.teammates?.map((teammate, index) => (
                                                <motion.div 
                                                    key={teammate.username}
                                                    className="flex items-center py-3 px-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3, delay: index * 0.1 }}
                                                >
                                                    <div className="flex-1 min-w-0 text-sm font-medium text-gray-600">
                                                        <span className="truncate block">
                                                            {index + 1}. {teammate.username.toUpperCase()}
                                                        </span>
                                                    </div>
                                                    <div className="flex space-x-2 flex-shrink-0">
                                                        <div className="text-center text-sm w-16 text-gray-600 font-normal">
                                                            {teammate.weekly_games_played}/{leagueStats.max_weekly_games}
                                                        </div>
                                                        <div className="text-center text-sm w-16 text-gray-600 font-normal">
                                                            {teammate.weekly_avg_score}
                                                        </div>
                                                        <div className="text-center text-sm w-16 text-gray-600 font-normal">
                                                            {teammate.weekly_total_score.toLocaleString('en-US')}
                                                        </div>
                                                    </div>
                                                </motion.div>
                                            ))}
                                            
                                            {/* Team Totals Row */}
                                            <div className="flex items-center py-3 px-4 border-t border-gray-300">
                                                <div className="font-bold leading-none text-gray-600 flex-1 min-w-0">
                                                    Total
                                                </div>
                                                <div className="flex space-x-2 flex-shrink-0">
                                                    <div className="text-center font-bold text-sm text-popashot-blue w-16">
                                                        {leagueStats.opponent.total_games_played}/{leagueStats.opponent.max_possible_games}
                                                    </div>
                                                    <div className="text-center font-bold text-sm text-popashot-blue w-16">
                                                        {leagueStats.opponent.team_avg}
                                                    </div>
                                                    <div className="text-center font-bold text-sm text-popashot-blue w-16">
                                                        {leagueStats.opponent.total_score.toLocaleString('en-US')}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Total Score Explanation - Show whenever user has team and league stats */}
                        {userData?.team && leagueStats && (
                            <div className={`mt-4 px-4 ${!userData?.league_mode_enabled ? 'opacity-50' : ''}`}>
                                <p className="text-center font-medium text-gray-500 text-xs">
                                    *Total from player's top {leagueStats.top_scores_counted} weekly games
                                </p>
                            </div>
                        )}

                    </div>
                )}

                {/* Active league's video verification card - after the team stats.
                    KNOWN INCONSISTENCY: this verifies only the player's latest game, while the Score per round layout
                    (LeagueGameScores) lists every game with its own upload button. To make Team totals leagues verify
                    every game, show that round list with upload buttons under this layout too. */}
                {leagueStats && !perGameLayout && activeLeague?.video_verification && (
                    <CompetitionCard comp={activeLeague} onRefresh={fetchCompetitions} />
                )}

                {/* XP Level Display */}
                {/*
                <div className="mt-6">
                    <h3 className="mb-4">Ranking</h3>
                        {!userData ? (
                            <div className="bg-white rounded-lg p-6 border border-popashot-grey-light">
                                <div className="h-24 bg-gray-200 rounded" />
                            </div>
                        ) : userData?.level_info && (
                            <div>
                                <XpLevelDisplay levelInfo={userData.level_info} />
                            </div>
                        )}
                </div>
                */}

                {allAchievements.length > 0 && (
                    <div>
                        <div className="mb-4 mt-6">
                            <h3 className="text-xl mb-2">
                                Achievements {!userData ? (
                                    <span className="inline-block w-16 h-6 bg-gray-200 rounded" />
                                ) : (
                                    `(${allAchievements.filter(a => a.earned).length}/${allAchievements.length})`
                                )}
                            </h3>
                        </div>
                        {!userData ? (
                            <div className="relative -mx-4">
                                <div className="flex space-x-2 pb-4 px-4">
                                    {[1, 2, 3].map((i) => (
                                        <div
                                            key={i}
                                            className="flex-none w-[calc(40%-0.5rem)] min-w-[calc(40%-0.5rem)] bg-white p-3 rounded-lg shadow-md"
                                        >
                                            <div className="flex flex-col items-center text-center">
                                                <div className="w-16 h-16 bg-gray-200 rounded mb-3" />
                                                <div className="w-full">
                                                    <div className="h-6 bg-gray-200 rounded w-3/4 mx-auto mb-2" />
                                                    <div className="h-12 bg-gray-200 rounded mb-3" />
                                                    <div className="flex flex-col items-center gap-2">
                                                        <div className="h-6 bg-gray-200 rounded-full w-16" />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <AchievementsDisplay
                                allAchievements={allAchievements}
                            />
                        )}
                    </div>
                )}
            </div>
            <EditProfileModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                currentUser={userData}
                onUpdate={fetchUserData}
            />
        </>
    );
};

export default Profile;
